import React, { useState } from 'react';
import apiClient from '../auth/service';

interface Props {
    targetSimCode: string;
    onImportComplete?: () => void;
}

const QuestionImportPanel: React.FC<Props> = ({ targetSimCode, onImportComplete }) => {
    const [fileName, setFileName] = useState<string>('');
    const [data, setData] = useState<any>(null);
    const [preview, setPreview] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [importing, setImporting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const handleFile = async (file: File) => {
        setError(null);
        setSuccess(null);
        setLoading(true);
        setPreview(null);
        try {
            const text = await file.text();
            let parsed: any;
            try {
                parsed = JSON.parse(text);
            } catch (e: any) {
                throw new Error('Arquivo JSON inválido');
            }
            setData(parsed);
            const payload = Array.isArray(parsed) ? { questions: parsed } : parsed;
            // Usar o header x-simulado para o preview também
            const result = await apiClient.post('/admin/questions/import/preview', payload, { headers: { 'x-simulado': targetSimCode } });
            setPreview(result);
            setFileName(file.name);
        } catch (e: any) {
            setError(e?.message || 'Falha ao processar arquivo');
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        if (!data) return;
        setError(null);
        setSuccess(null);
        setImporting(true);
        try {
            const payload = Array.isArray(data) ? { questions: data } : data;
            // Forçar o simCode no header
            await apiClient.post('/admin/questions/import', payload, { headers: { 'x-simulado': targetSimCode } });
            setSuccess('Importação concluída com sucesso!');
            if (onImportComplete) onImportComplete();
        } catch (e: any) {
            setError(e?.message || 'Falha ao importar questões');
        } finally {
            setImporting(false);
        }
    };

    const clearAll = () => {
        setFileName('');
        setData(null);
        setPreview(null);
        setError(null);
        setSuccess(null);
    };

    const changes = preview?.changes || [];
    const summary = preview?.summary || null;

    const renderValue = (v: any) => {
        if (v === null || typeof v === 'undefined') return '—';
        if (typeof v === 'object') return JSON.stringify(v);
        return String(v);
    };

    return (
        <div className="p-4 bg-gray-50 dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700">
            <h5 className="text-md font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <i className="fas fa-file-import text-indigo-500"></i>
                Importar Questões para: <span className="text-indigo-600 dark:text-indigo-400 font-mono">{targetSimCode}</span>
            </h5>
            
            {error && <p className="text-red-500 mb-4 text-sm bg-red-50 dark:bg-red-900/20 p-2 rounded">Erro: {error}</p>}
            {success && <p className="text-green-500 mb-4 text-sm bg-green-50 dark:bg-green-900/20 p-2 rounded">{success}</p>}
            
            <div className="flex flex-col sm:flex-row items-center gap-3 mb-4">
                <input 
                    type="file" 
                    accept=".json" 
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} 
                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-indigo-900/30 dark:file:text-indigo-400" 
                />
                {fileName && (
                    <button onClick={clearAll} className="text-xs text-red-500 hover:underline">Limpar</button>
                )}
            </div>

            {loading && <div className="text-center py-4 text-gray-500 animate-pulse text-sm">Analisando arquivo...</div>}
            
            {summary && (
                <div className="mb-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="bg-white dark:bg-slate-800 p-2 rounded border dark:border-slate-700 text-center">
                        <div className="text-[10px] text-gray-500 uppercase">Total</div>
                        <div className="font-bold text-orange-400">{summary.total}</div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-2 rounded border dark:border-slate-700 text-center">
                        <div className="text-[10px] text-gray-500 uppercase">Novas</div>
                        <div className="font-bold text-green-400">{summary.newCount}</div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-2 rounded border dark:border-slate-700 text-center">
                        <div className="text-[10px] text-gray-500 uppercase">Alteradas</div>
                        <div className="font-bold text-yellow-400">{summary.changedCount}</div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-2 rounded border dark:border-slate-700 text-center">
                        <div className="text-[10px] text-gray-500 uppercase">Iguais</div>
                        <div className="font-bold text-slate-400">{summary.unchangedCount}</div>
                    </div>
                </div>
            )}

            {Array.isArray(changes) && changes.length > 0 && (
                <div className="overflow-x-auto max-h-40 mb-4 border dark:border-slate-700 rounded shadow-inner">
                    <table className="min-w-full bg-white dark:bg-slate-800 text-[10px]">
                        <thead className="sticky top-0 bg-gray-100 dark:bg-slate-700">
                            <tr>
                                <th className="py-1 px-2 text-left">ID</th>
                                <th className="py-1 px-2 text-left">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {changes.slice(0, 50).map((c: any) => (
                                <tr key={c.id} className="border-b dark:border-slate-700">
                                    <td className="py-1 px-2">{c.id}</td>
                                    <td className={`py-1 px-2 font-bold ${c.status === 'new' ? 'text-green-500' : c.status === 'changed' ? 'text-yellow-500' : 'text-gray-400'}`}>{c.status}</td>
                                </tr>
                            ))}
                            {changes.length > 50 && (
                                <tr><td colSpan={2} className="text-center py-1 text-gray-400">... e mais {changes.length - 50} questões</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <button 
                onClick={handleImport} 
                disabled={!data || importing} 
                className={`w-full py-2 rounded-lg text-white font-bold transition-all ${!data || importing ? 'bg-slate-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 shadow-md hover:shadow-lg active:scale-95'}`}
            >
                {importing ? (
                    <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i> Importando...
                    </span>
                ) : 'Confirmar Importação'}
            </button>
        </div>
    );
};

export default QuestionImportPanel;
